import { BrowserModule } from '@angular/platform-browser';
import {
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  FullscreenOverlayContainer,
  OverlayContainer,
} from '@angular/cdk/overlay';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import { NG_STATUS_CLASSES } from '@angular/forms/signals/compat';
import {
  NavigationActionTiming,
  RouterStateSerializer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';

import { effects, reducers } from './store';
import { CustomSerializer } from './store/reducers/router.reducer';
import moment from 'moment';

import { AppComponent, AuthService } from '@fc-core';
import { JwtInterceptor } from './auth/jwt.interceptor';
import { proxiedHostsInterceptor } from './core/interceptors/proxied-hosts.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { NotSupportedComponent } from './core/not-supported/not-supported.component';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { AppInitService } from './core/services/app-init.service';
import { OtaSplashScreenComponent } from '@fc-core/platform/ota-update/ota-splash-screen.component';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { AppColors, AppColorsFactory } from '@fc-core/constants/colors';

import { AuthGuard } from './auth/auth-guard.service';
import { FilesUploadComponent } from '@fc-core/files-uploads/files-upload.component';
import { GlobalErrorHandler } from './core/errors/global-error-handler';
import { providePlatform } from './core/platform/platform.providers';

moment.relativeTimeThreshold('s', 60);
moment.relativeTimeThreshold('m', 60);
moment.relativeTimeThreshold('h', 24);
moment.relativeTimeThreshold('d', 30);
moment.relativeTimeThreshold('M', 12);

moment.relativeTimeRounding(Math.floor);

@NgModule({
  declarations: [AppComponent, NotSupportedComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    StoreRouterConnectingModule.forRoot({
      serializer: CustomSerializer,
      navigationActionTiming: NavigationActionTiming.PostActivation,
    }),
    // NGRX
    StoreModule.forRoot(reducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: true,
        strictActionWithinNgZone: false,
      },
    }),
    EffectsModule.forRoot(effects),
    OtaSplashScreenComponent,
    FilesUploadComponent,
  ],
  providers: [
    provideZoneChangeDetection(),
    providePlatform(),
    provideSignalFormsConfig({ classes: NG_STATUS_CLASSES }),
    AuthService,
    AuthGuard,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
    },
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideSingleSelectionIndicator: true },
    },
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    AppInitService,
    provideAppInitializer(() => inject(AppInitService).init()),
    provideHttpClient(
      withXhr(),
      withInterceptors([proxiedHostsInterceptor]),
      withInterceptorsFromDi(),
    ),
    {
      provide: AppColors,
      useFactory: AppColorsFactory,
    },
    {
      provide: OverlayContainer,
      useClass: FullscreenOverlayContainer,
    },
  ],
})
export class AppModule {}
